import {ThreeFacade} from '../ThreeFacade';
import {TPoint2D} from '../../../../../../../common-code/types/TPoint2D';
import {TLine} from '../../../../../../../common-code/types/TLine';
import {
    FACADE_DEFAULT_BEVEL_SEGMENTS,
    FACADE_DEFAULT_BEVEL_SIZE,
    FACADE_DEFAULT_PLANE_BEVEL_SIZE,
} from '../../../../../constants';
import {
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    ExtrudeGeometry,
    FrontSide,
    LineSegments,
    Material,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    Shape
} from 'three';
import {
    FACADE_DETAIL_TYPE_MAIN,
    FACADE_MODEL_TYPE_GLASS, FACADE_MODEL_TYPE_GLASS2,
    FACADE_MODEL_VERSION_NONE
} from '../../../../../../../common-code/constants';
import {MathHelper} from 'common-code';
import {ExtrudeGeometryOptions} from 'three/src/geometries/ExtrudeGeometry';
import {InterleavedBufferAttribute} from 'three/src/core/InterleavedBufferAttribute';
import {GLBufferAttribute} from 'three/src/core/GLBufferAttribute';
import {IModelTransformData} from '../../../../../../../common-code/interfaces/3d/IModelTransformData';
import {IFacadeModelData} from '../../../../../../../common-code/interfaces/materials/IFacadeModelData';
import {TPoint3D} from '../../../../../../../common-code/types/TPoint3D';
import {CommonHelper} from 'common-code';

export class ThreeSquareFacade extends ThreeFacade {

    public setDummyTransparent(value: boolean) {
        let child: Object3D;
        let childMesh: Mesh;
        let material: Material;

        for (child of this.dummy.children) {
            if (child.name === 'dummyBody' && 'isMesh' in child) {
                childMesh = child as Mesh;
                if (Array.isArray(childMesh.material)) {
                    for (material of childMesh.material) {
                        material.transparent = value;
                    }
                } else if ('isMaterial' in childMesh.material) {
                    childMesh.material.transparent = value;
                }
            }
        }
    }

    protected addToScene() {
        this.view3d.name = 'ThreeSquareFacade';
        this.unit.view3d.add(this.view3d);
    }

    protected getShapePoints(): TPoint2D[] {
        return [
            {x: -this.getWidth() / 2, y: -this.getHeight() / 2},
            {x: -this.getWidth() / 2, y: this.getHeight() / 2},
            {x: this.getWidth() / 2, y: this.getHeight() / 2},
            {x: this.getWidth() / 2, y: -this.getHeight() / 2},
        ];
    }

    protected getBevelShapePoints(bevelSize: number): TPoint2D[] {
        let points: TPoint2D[];
        let bevelPoints: TPoint2D[];
        let prevPoint: TPoint2D;
        let point: TPoint2D;
        let nextPoint: TPoint2D;
        let index;

        points = this.getShapePoints();
        if (bevelSize === 0) {
            return points;
        }
        bevelPoints = [];
        for (index = 0; index < points.length; index++) {
            if (points.hasOwnProperty(index)) {
                if (index === 0) {
                    prevPoint = points[points.length - 1];
                    point = points[index];
                    nextPoint = points[index + 1];
                } else if (index === points.length - 1) {
                    prevPoint = points[index - 1];
                    point = points[index];
                    nextPoint = points[0];
                } else {
                    prevPoint = points[index - 1];
                    point = points[index];
                    nextPoint = points[index + 1];
                }
                bevelPoints.push(this.getPointWithBevel(prevPoint, point, nextPoint, bevelSize));
            }
        }

        return bevelPoints;
    }

    protected getPointWithBevel(
        prevPoint: TPoint2D,
        point: TPoint2D,
        nextPoint: TPoint2D,
        bevelSize: number
    ): TPoint2D {
        let prevLine: TLine, line: TLine, resultPoint: TPoint2D | undefined;

        prevLine = MathHelper.getParallelLinePoints({x: prevPoint.x, y: prevPoint.y}, {
            x: point.x,
            y: point.y
        }, bevelSize);
        line = MathHelper.getParallelLinePoints({x: point.x, y: point.y}, {
            x: nextPoint.x,
            y: nextPoint.y
        }, bevelSize);

        if (prevLine && line) {
            resultPoint = MathHelper.getIntersectionPoint(prevLine, line);
        }
        if (!resultPoint) {
            resultPoint = {
                x: point.x,
                y: point.y
            };
        }
        return resultPoint;
    }

    protected createDummyGeometry(): BufferGeometry {
        let geometry: BufferGeometry;
        let extrudeSettings: ExtrudeGeometryOptions;
        let position: BufferAttribute | InterleavedBufferAttribute | GLBufferAttribute;
        let index: number;
        let length: number;

        extrudeSettings = {
            steps: 1,
            depth: this.getDepth() - FACADE_DEFAULT_BEVEL_SIZE,
            bevelEnabled: true,
            bevelThickness: FACADE_DEFAULT_BEVEL_SIZE,
            bevelSize: FACADE_DEFAULT_BEVEL_SIZE,
            bevelSegments: FACADE_DEFAULT_BEVEL_SEGMENTS
        };

        geometry = new ExtrudeGeometry(
            this.createShapeWithBevel(FACADE_DEFAULT_BEVEL_SIZE),
            extrudeSettings
        );
        position = geometry.attributes.position;
        for (index = 0, length = position.count; index < length; index++) {
            if ('isGLBufferAttribute' in position) {
                continue;
            }
            if (position.getZ(index) < 0) {
                position.setZ(index, 0)
            }
        }

        return geometry;
    }

    protected createShapeWithBevel(bevelSize: number): Shape {
        let index: number;
        let bevelPoints: TPoint2D[];
        let shape: Shape;

        shape = new Shape();
        bevelPoints = this.getBevelShapePoints(bevelSize);
        shape.moveTo(bevelPoints[0].x, bevelPoints[0].y);
        for (index = 1; index < bevelPoints.length; index++) {
            if (bevelPoints.hasOwnProperty(index)) {
                shape.lineTo(bevelPoints[index].x, bevelPoints[index].y);
            }
        }
        shape.lineTo(bevelPoints[0].x, bevelPoints[0].y);

        return shape;
    }

    protected addPlaneThreeModel() {
        let body: Mesh;

        body = new Mesh(
            this.createPlaneGeometry(),
            this.getMaterialByDetailType(FACADE_DETAIL_TYPE_MAIN)
        );
        body.name = 'planeBody';
        body.matrixAutoUpdate = false;
        body.castShadow = true;
        body.receiveShadow = true;
        this.threeModel.add(body);
        this.afterLoadThreeModel();
    }

    protected getPlaneBevel(): number {
        if (this.facadeData.planeBevel !== undefined && !isNaN(+this.facadeData.planeBevel)) {
            return this.facadeData.planeBevel;
        }
        return FACADE_DEFAULT_PLANE_BEVEL_SIZE;
    }

    protected createPlaneGeometry(): BufferGeometry {
        let geometry: BufferGeometry;
        let extrudeSettings: ExtrudeGeometryOptions;
        let position: BufferAttribute | InterleavedBufferAttribute | GLBufferAttribute;
        let index: number;
        let length: number;
        let planeBevel: number;

        planeBevel = this.getPlaneBevel();
        extrudeSettings = {
            steps: 1,
            depth: this.getDepth() - planeBevel,
            bevelEnabled: true,
            bevelThickness: planeBevel,
            bevelSize: planeBevel,
            bevelSegments: planeBevel
        };

        geometry = new ExtrudeGeometry(this.createShapeWithBevel(planeBevel), extrudeSettings);
        position = geometry.attributes.position;
        for (index = 0, length = position.count; index < length; index++) {
            if ('isGLBufferAttribute' in position) {
                continue;
            }
            if (position.getZ(index) < 0) {
                position.setZ(index, 0)
            }
        }

        return geometry;
    }

    protected createDummy() {
        let body: Mesh;

        this.dummy.matrixAutoUpdate = false;
        body = new Mesh(this.createDummyGeometry(), new MeshBasicMaterial({
            transparent: true,
            opacity: 0.4,
            side: FrontSide,
        }));
        body.name = 'dummyBody';
        body.matrixAutoUpdate = false;
        this.dummy.add(body);
        this.createMeshCarcass(body, '#929292', 'dummyCarcass');
        if ([FACADE_MODEL_TYPE_GLASS, FACADE_MODEL_TYPE_GLASS2].includes(this.getModelType())) {
            this.createDummyGlass();
        }
        this.dummy.name = 'Dummy';
        this.view3d.add(this.dummy);
    }

    protected createDummyGlass() {
        let geometry: BufferGeometry;
        let glass: Mesh;
        let glassCarcass: LineSegments;

        geometry = new BoxGeometry(this.getWidth() - 120, this.getHeight() - 120, this.getDepth());
        glass = new Mesh(geometry, new MeshBasicMaterial({
            transparent: true,
            opacity: 0.1,
        }));
        glass.name = 'dummyGlass';
        glass.matrixAutoUpdate = false;
        glass.position.z = 1;
        this.dummy.add(glass);
        glassCarcass = this.createMeshCarcass(glass, '#929292', 'dummyGlassCarcass');
        glassCarcass.position.z = 1;
        this.dummy.add(glassCarcass);
    }

    protected scaleThreeModel() {
        if (!this.scaleThreeModelByDetails()) {
            if (this.threeModelData) {
                this.threeModel.scale.setY(this.getHeight()/this.threeModelData.height);
                this.threeModel.scale.setX(this.getWidth()/this.threeModelData.width);
            }
            this.updateAllMatrices();
        }
    }

    protected scaleThreeModelByDetails(): boolean {
        let isScale: boolean = false;
        let transformData: IModelTransformData;
        let threeModelData: IFacadeModelData;
        if (this.threeModelData) {
            threeModelData = this.threeModelData;
            this.threeModel.traverse((child) => {
                if ('isMesh' in child &&
                    child instanceof Mesh) {
                    if (child.userData.transformData) {
                        transformData = child.userData.transformData as IModelTransformData;
                        // Если не опредили версию меша, то пропускаем
                        if (transformData.version === FACADE_MODEL_VERSION_NONE) {
                            return;
                        }
                        isScale = true;
                        child.geometry = child.geometry.clone();
                        this.transformDetailScale(child, transformData.scale, threeModelData);
                        this.transformDetailTranslate(child, transformData.translate, threeModelData);
                    }
                }
            });
        }
        if (isScale) {
            this.updateAllMatrices();
        }

        return isScale;
    }

    protected transformDetailScale(
        detail: Mesh,
        scale: TPoint3D,
        threeModelData: IFacadeModelData
    ) {
        if (scale.x) {
            detail.scale.setX(this.getWidth()/threeModelData.width);
        }
        if (scale.y) {
            detail.scale.setY(this.getHeight()/threeModelData.height);
        }
        if (scale.z) {
            detail.scale.setY(this.getDepth()/threeModelData.depth);
        }
    }

    protected transformDetailTranslate(
        detail: Mesh,
        translate: TPoint3D,
        threeModelData: IFacadeModelData
    ) {
        let position: BufferAttribute | InterleavedBufferAttribute | GLBufferAttribute;
        let uv: BufferAttribute | InterleavedBufferAttribute | GLBufferAttribute;
        let uvItem: TPoint2D;
        let newUvItem: TPoint2D;
        let positionItem: TPoint3D;
        let newPositionItem: TPoint3D;
        let index: number;
        let length: number;
        let borderUv: TPoint2D;
        let direction: -1 | 1;

        position = detail.geometry.attributes.position;
        uv = detail.geometry.attributes.uv;
        for (index = 0, length = position.count; index < length; index++) {
            if ('isGLBufferAttribute' in position ||
                'isGLBufferAttribute' in uv) {
                continue;
            }
            positionItem = {
                x: position.getX(index),
                y: position.getY(index),
                z: position.getZ(index)
            }
            newPositionItem = {...positionItem};
            uvItem = {
                x: uv.getX(index),
                y: uv.getY(index),
            }
            newUvItem = {...uvItem};
            borderUv = {x: 0, y: 0};
            if (translate.x) {
                direction = positionItem.x > 0 ? 1 : -1;
                newPositionItem.x = direction * (this.getWidth() / 2 - (threeModelData.width / 2 - Math.abs(positionItem.x)));
                borderUv.x = direction < 0 ?
                    (threeModelData.width * uvItem.x) :
                    (threeModelData.width - threeModelData.width * uvItem.x);
                newUvItem.x = (direction < 0 ?
                    (borderUv.x / this.getWidth()) :
                    ((this.getWidth() - borderUv.x) / this.getWidth())) * (this.getWidth() / threeModelData.width);
            }
            if (translate.y) {
                direction = positionItem.y > 0 ? 1 : -1;
                newPositionItem.y = direction * (this.getHeight() / 2 - (threeModelData.height / 2 - Math.abs(positionItem.y)));
                borderUv.y = direction > 0 ?
                    (threeModelData.height * uvItem.y) :
                    (threeModelData.height - threeModelData.height * uvItem.y);
                newUvItem.y = (direction > 0 ?
                    (borderUv.y / this.getHeight()) :
                    ((this.getHeight() - borderUv.y) / this.getHeight())) * (this.getHeight() / threeModelData.height);
            }
            if (translate.z) {
                newPositionItem.z = (this.getDepth() - (threeModelData.depth - positionItem.z));
            }
            if (!CommonHelper.deepCompare(positionItem, newPositionItem)) {
                position.setXYZ(index, newPositionItem.x, newPositionItem.y, newPositionItem.z);
            }
            if (!CommonHelper.deepCompare(uvItem, newUvItem)) {
                uv.setXY(index, newUvItem.x, newUvItem.y);
            }
        }
        position.needsUpdate = true;
        uv.needsUpdate = true;
        detail.geometry.applyMatrix4(detail.matrix);
    }
}